import React, { useMemo, useState } from "react";
import {
	CartesianGrid,
	Legend,
	Line,
	LineChart,
	ResponsiveContainer,
	Tooltip,
	XAxis,
	YAxis,
} from "recharts";
import { ENGINE_SERIES_META } from "./monitoringAdapters";
import MonitoringExpandModal, {
	MonitoringChartDataTable,
	MonitoringExpandButton,
} from "./MonitoringExpandModal";

function resolveSeries(engineKeys) {
	const keys = engineKeys?.length
		? engineKeys
		: ENGINE_SERIES_META.map((s) => s.key).filter((k) => k !== "aiOverviews");
	const seen = new Set();
	return keys
		.map((key) => {
			const meta = ENGINE_SERIES_META.find((s) => s.key === key);
			return {
				key,
				label: meta?.label || key,
				color: meta?.color || "#6B7280",
			};
		})
		.filter((s) => {
			if (seen.has(s.label)) return false;
			seen.add(s.label);
			return true;
		});
}

function EngineLineChart({ data, series, height, yMax = 100 }) {
	return (
		<ResponsiveContainer width="100%" height={height}>
			<LineChart data={data} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
				<CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" vertical={false} />
				<XAxis
					dataKey="week"
					tick={{ fontSize: 12, fill: "#6B7280" }}
					axisLine={{ stroke: "#E5E7EB" }}
					tickLine={false}
				/>
				<YAxis
					domain={[0, yMax]}
					tick={{ fontSize: 12, fill: "#6B7280" }}
					axisLine={false}
					tickLine={false}
					width={32}
				/>
				<Tooltip
					formatter={(value) => [`${value}%`, ""]}
					contentStyle={{
						borderRadius: 8,
						border: "1px solid #E5E7EB",
						fontSize: 12,
					}}
				/>
				<Legend
					wrapperStyle={{ fontSize: 12, paddingTop: 8 }}
					iconType="circle"
					iconSize={8}
				/>
				{series.map((s) => (
					<Line
						key={s.key}
						type="monotone"
						dataKey={s.key}
						name={s.label}
						stroke={s.color}
						strokeWidth={2}
						dot={false}
						activeDot={{ r: 4 }}
					/>
				))}
			</LineChart>
		</ResponsiveContainer>
	);
}

export default function SovByEngineChart({
	data,
	engineKeys,
	title = "Share of voice by engine — recent runs",
	caption = null,
	yMax = 100,
}) {
	const [expanded, setExpanded] = useState(false);
	const series = useMemo(() => resolveSeries(engineKeys), [engineKeys]);
	const tableColumns = useMemo(
		() => [
			{ key: "week", label: "Week" },
			...series.map((s) => ({
				key: s.key,
				label: s.label,
				numeric: true,
				render: (row) =>
					row[s.key] != null ? `${row[s.key]}%` : "—",
			})),
		],
		[series]
	);

	const empty = !data?.length;

	return (
		<article className="monitoring__panel monitoring__panel--chart">
			<div className="monitoring__panel-header">
				<h3 className="monitoring__panel-title">{title}</h3>
				{!empty && <MonitoringExpandButton onClick={() => setExpanded(true)} />}
			</div>
			{caption && <p className="monitoring__panel-caption mb-2">{caption}</p>}
			{empty ? (
				<p className="monitoring__empty mb-0">No engine series yet.</p>
			) : (
				<div className="monitoring__chart-wrap">
					<EngineLineChart data={data} series={series} height={280} yMax={yMax} />
				</div>
			)}

			<MonitoringExpandModal
				open={expanded}
				onClose={() => setExpanded(false)}
				title={title}
				chart={
					<div className="monitoring__chart-wrap monitoring__chart-wrap--expand">
						<EngineLineChart data={data} series={series} height={360} yMax={yMax} />
					</div>
				}
				table={
					<MonitoringChartDataTable
						caption={title}
						columns={tableColumns}
						rows={data}
					/>
				}
			/>
		</article>
	);
}
